// File upload utilities for the attachment system

import { supabase } from '../../lib/supabase';
import { FileAttachment, FileUploadOptions, FileUploadProgress } from '../../types/file-attachments';
import { validateForContext, validateFileName, validateFileContent, scanFileForVirus, generateFileHash } from './validation';
import { 
  trackFileOperation, 
  trackAsync, 
  BackgroundTaskTracker,
  trackPerformance 
} from '@/utils/error-tracking/comprehensive-tracking';

export interface UploadResult {
  success: boolean;
  attachment?: FileAttachment;
  error?: string;
}

export interface BatchUploadResult {
  successful: FileAttachment[];
  failed: { file: File; error: string }[];
  total: number;
}

export class FileUploadManager {
  private onProgressUpdate?: (progress: FileUploadProgress[]) => void;
  private uploads: Map<string, FileUploadProgress> = new Map();

  constructor(onProgressUpdate?: (progress: FileUploadProgress[]) => void) {
    this.onProgressUpdate = onProgressUpdate;
  }

  async uploadFile(
    file: File,
    options: FileUploadOptions
  ): Promise<UploadResult> {
    const uploadId = this.generateUploadId();
    const taskTracker = new BackgroundTaskTracker('fileUpload', {
      fileName: file.name,
      fileSize: file.size,
      options
    });
    
    try {
      // Initialize progress tracking
      const progress: FileUploadProgress = {
        file,
        progress: 0,
        status: 'waiting'
      };
      
      this.uploads.set(uploadId, progress);
      this.notifyProgress();

      // Validate file
      progress.status = 'uploading';
      progress.progress = 10;
      this.notifyProgress();

      taskTracker.checkpoint('validation_start');
      const validationResult = await trackAsync(
        () => validateForContext(file, options.entity_type),
        {
          operation: 'validateFileContext',
          component: 'FileUploadManager',
          metadata: { fileName: file.name, entityType: options.entity_type }
        }
      );
      
      if (!validationResult.isValid) {
        progress.status = 'error';
        progress.error = validationResult.errors.join(', ');
        this.notifyProgress();
        await taskTracker.fail(new Error(validationResult.errors.join(', ')), {
          stage: 'validation'
        });
        
        return {
          success: false,
          error: validationResult.errors.join(', ')
        };
      }
      taskTracker.checkpoint('validation_complete', { valid: true });
      
      // Enhanced file name validation
      const nameValidation = validateFileName(file.name);
      if (!nameValidation.isValid) {
        progress.status = 'error';
        progress.error = nameValidation.errors.join(', ');
        this.notifyProgress();
        
        return {
          success: false,
          error: nameValidation.errors.join(', ')
        };
      }
      
      // Validate file content
      const contentValidation = await validateFileContent(file);
      if (!contentValidation.isValid) {
        progress.status = 'error';
        progress.error = contentValidation.errors.join(', ');
        this.notifyProgress();
        
        return {
          success: false,
          error: contentValidation.errors.join(', ')
        };
      }
      
      // Basic virus scan
      const virusScan = await scanFileForVirus(file);
      if (!virusScan.isClean) {
        progress.status = 'error';
        progress.error = 'File contains suspicious content: ' + virusScan.threats.join(', ');
        this.notifyProgress();
        
        return {
          success: false,
          error: 'File contains suspicious content'
        };
      }

      // Get user first for secure path generation
      const user = (await supabase.auth.getUser()).data.user;
      
      if (!user) {
        progress.status = 'error';
        progress.error = 'User not authenticated';
        this.notifyProgress();
        
        return {
          success: false,
          error: 'User not authenticated'
        };
      }
      
      // Generate file path and metadata with sanitized name
      const fileName = this.generateUniqueFileName(nameValidation.sanitizedFileName);
      const filePath = this.generateSecurePath(fileName, options, user.id);
      
      progress.progress = 20;
      this.notifyProgress();

      // Upload to Supabase Storage
      taskTracker.checkpoint('upload_start');
      const uploadResult = await trackFileOperation(
        async () => {
          const { data, error } = await supabase.storage
            .from('attachments')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
              metadata: {
                originalName: file.name,
                uploadedBy: (await supabase.auth.getUser()).data.user?.id || 'unknown',
                entityType: options.entity_type,
                entityId: options.entity_id || ''
              }
            });

          if (error) throw error;
          return data;
        },
        {
          fileName: file.name,
          operation: 'upload',
          fileSize: file.size,
          fileType: file.type
        }
      );

      const uploadData = uploadResult;
      taskTracker.checkpoint('upload_complete', { path: uploadData.path });

      progress.progress = 60;
      this.notifyProgress();

      // Create database record
      const checksum = await generateFileHash(file);

      const attachmentData = {
        filename: fileName,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type,
        file_extension: this.getFileExtension(file.name),
        storage_path: uploadData.path,
        checksum,
        title: options.title,
        description: options.description,
        tags: options.tags || [],
        uploaded_by: user.id,
        uploader_name: this.getUserName(user),
        school_id: this.getUserSchoolId(user) || null,
        entity_type: options.entity_type,
        entity_id: options.entity_id,
        access_level: options.access_level || 'private',
        allowed_roles: options.allowed_roles || [],
        expiry_date: options.expiry_date?.toISOString(),
        status: 'processing' as const
      };

      progress.progress = 80;
      this.notifyProgress();

      taskTracker.checkpoint('db_insert_start');
      const { data: attachment, error: dbError } = await trackAsync(
        async () => {
          const result = await supabase
            .from('file_attachments')
            .insert(attachmentData)
            .select()
            .single();
          
          if (result.error) throw result.error;
          return result;
        },
        {
          operation: 'insertFileRecord',
          component: 'FileUploadManager',
          metadata: { fileName: file.name, fileSize: file.size }
        }
      );

      if (dbError) {
        // Clean up uploaded file
        await trackFileOperation(
          () => supabase.storage.from('attachments').remove([uploadData.path]),
          {
            fileName: file.name,
            operation: 'delete',
            fileSize: file.size,
            fileType: file.type
          }
        );
        
        progress.status = 'error';
        progress.error = dbError.message;
        this.notifyProgress();
        await taskTracker.fail(dbError, { stage: 'database_insert' });
        
        return {
          success: false,
          error: dbError.message
        };
      }
      taskTracker.checkpoint('db_insert_complete', { attachmentId: attachment.id });

      // Mark as ready
      const { data: updatedAttachment, error: updateError } = await supabase
        .from('file_attachments')
        .update({ status: 'ready' })
        .eq('id', attachment.id)
        .select()
        .single();

      progress.status = 'success';
      progress.progress = 100;
      progress.attachment_id = attachment.id;
      this.notifyProgress();

      // Log the upload
      await this.logFileAccess(attachment.id, 'version_create');

      await taskTracker.complete({
        attachmentId: attachment.id,
        fileName: file.name,
        fileSize: file.size
      });

      return {
        success: true,
        attachment: updatedAttachment || attachment
      };

    } catch (error) {
      const progress = this.uploads.get(uploadId);
      if (progress) {
        progress.status = 'error';
        progress.error = error instanceof Error ? error.message : 'Unknown error';
        this.notifyProgress();
      }
      
      await taskTracker.fail(error as Error, {
        uploadId,
        fileName: file.name
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      // Clean up progress tracking after a delay
      setTimeout(() => {
        this.uploads.delete(uploadId);
        this.notifyProgress();
      }, 5000);
    }
  }

  async uploadMultipleFiles(
    files: File[],
    options: FileUploadOptions
  ): Promise<BatchUploadResult> {
    const perf = trackPerformance('batchFileUpload');
    const successful: FileAttachment[] = [];
    const failed: { file: File; error: string }[] = [];

    perf.mark('batch_start');

    // Upload files in parallel with concurrency limit
    const concurrency = 3;
    const chunks = this.chunkArray(files, concurrency);

    for (const chunk of chunks) {
      const promises = chunk.map(file => this.uploadFile(file, options));
      const results = await Promise.all(promises);

      results.forEach((result, index) => {
        if (result.success && result.attachment) {
          successful.push(result.attachment);
        } else {
          failed.push({
            file: chunk[index],
            error: result.error || 'Unknown error'
          });
        }
      });
    }

    perf.mark('batch_end');
    await perf.measure('batch_start', 'batch_end');

    return {
      successful,
      failed,
      total: files.length
    };
  }

  private generateUploadId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  private generateUniqueFileName(originalName: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const extension = this.getFileExtension(originalName);
    const baseName = originalName.replace(extension, '').replace(/[^a-zA-Z0-9]/g, '_');
    
    return `${baseName}_${timestamp}_${random}${extension}`;
  }

  private generateFilePath(fileName: string, options: FileUploadOptions): string {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    
    let basePath = `${year}/${month}`;
    
    if (options.entity_type) {
      basePath += `/${options.entity_type}`;
    }
    
    if (options.entity_id) {
      basePath += `/${options.entity_id}`;
    }
    
    return `${basePath}/${fileName}`;
  }
  
  private generateSecurePath(fileName: string, options: FileUploadOptions, userId: string): string {
    const timestamp = Date.now();
    const random = crypto.getRandomValues(new Uint8Array(8));
    const hex = Array.from(random).map(b => b.toString(16).padStart(2, '0')).join('');
    
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    
    // User-isolated path structure
    let basePath = `users/${userId}/${year}/${month}`;
    
    if (options.entity_type) {
      basePath += `/${options.entity_type}`;
    }
    
    if (options.entity_id) {
      basePath += `/${options.entity_id}`;
    }
    
    // Include timestamp and random hex to prevent collisions
    return `${basePath}/${timestamp}_${hex}_${fileName}`;
  }

  private getFileExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot === -1 ? '' : filename.substring(lastDot);
  }

  private getUserName(user: any): string {
    return user.user_metadata?.full_name || 
           user.user_metadata?.name || 
           user.email || 
           'Unknown User';
  }

  private getUserSchoolId(user: any): string | undefined {
    return user.user_metadata?.school_id || user.raw_user_meta_data?.school_id;
  }


  private async logFileAccess(fileId: string, action: string): Promise<void> {
    try {
      await supabase
        .from('file_access_logs')
        .insert({
          file_id: fileId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action,
          additional_data: {
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        });
    } catch (error) {
      console.warn('Failed to log file access:', error);
    }
  }

  private notifyProgress(): void {
    if (this.onProgressUpdate) {
      const progressArray = Array.from(this.uploads.values());
      this.onProgressUpdate(progressArray);
    }
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  getActiveUploads(): FileUploadProgress[] {
    return Array.from(this.uploads.values());
  }

  clearCompletedUploads(): void {
    const activeStatuses = ['waiting', 'uploading', 'processing'];
    for (const [id, progress] of this.uploads.entries()) {
      if (!activeStatuses.includes(progress.status)) {
        this.uploads.delete(id);
      }
    }
    this.notifyProgress();
  }
}

// Secure file access with authorization
export async function getSecureFileUrl(
  fileId: string,
  action: 'view' | 'download' = 'view'
): Promise<string> {
  return await trackAsync(
    async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Check user authorization for the file
      const hasAccess = await checkFileAccess(fileId, user.id);
      if (!hasAccess) {
        throw new Error('Unauthorized file access');
      }
  
  // Get file record
  const { data: file, error: fileError } = await supabase
    .from('file_attachments')
    .select('*')
    .eq('id', fileId)
    .single();
    
  if (fileError || !file) {
    throw new Error('File not found');
  }
  
      // Generate short-lived signed URL (1 minute for download, 5 minutes for view)
      const expirySeconds = action === 'download' ? 60 : 300;
      const { data, error } = await supabase.storage
        .from('attachments')
        .createSignedUrl(file.storage_path, expirySeconds);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Log access for audit
      await logFileAccess(fileId, user.id, action);
      
      // Update access timestamp
      await supabase
        .from('file_attachments')
        .update({ 
          last_accessed: new Date().toISOString(),
          download_count: action === 'download' ? file.download_count + 1 : file.download_count
        })
        .eq('id', fileId);
      
      return data.signedUrl;
    },
    {
      operation: 'getSecureFileUrl',
      component: 'FileAccess',
      metadata: { fileId, action }
    }
  );
}

// Check if user has access to a file
async function checkFileAccess(fileId: string, userId: string): Promise<boolean> {
  const { data: file, error } = await supabase
    .from('file_attachments')
    .select(`
      *,
      uploaded_by,
      access_level,
      allowed_roles,
      school_id,
      entity_type,
      entity_id,
      expiry_date
    `)
    .eq('id', fileId)
    .single();
    
  if (error || !file) {
    return false;
  }
  
  // Check if file is expired
  if (file.expiry_date && new Date(file.expiry_date) < new Date()) {
    return false;
  }
  
  // Check if file is deleted
  if (file.status === 'deleted') {
    return false;
  }
  
  // Owner always has access
  if (file.uploaded_by === userId) {
    return true;
  }
  
  // Check access level
  switch (file.access_level) {
    case 'public':
      return true;
      
    case 'private':
      return false; // Only owner can access
      
    case 'school_only':
      // Check if user belongs to the same school
      const { data: userData } = await supabase
        .from('users')
        .select('school_id')
        .eq('id', userId)
        .single();
      return userData?.school_id === file.school_id;
      
    case 'admin_only':
      // Check if user has admin role
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();
      return roleData?.role === 'admin';
      
    default:
      return false;
  }
}

// Enhanced logging function
async function logFileAccess(fileId: string, userId: string, action: string): Promise<void> {
  try {
    await supabase
      .from('file_access_logs')
      .insert({
        file_id: fileId,
        user_id: userId,
        action,
        ip_address: await getUserIpAddress(),
        user_agent: navigator.userAgent,
        additional_data: {
          timestamp: new Date().toISOString(),
          referrer: document.referrer,
          screen_resolution: `${window.screen.width}x${window.screen.height}`
        }
      });
  } catch (error) {
    console.warn('Failed to log file access:', error);
  }
}

// Get user IP address (requires server-side implementation)
async function getUserIpAddress(): Promise<string | null> {
  try {
    // This would typically call a server endpoint that returns the client IP
    // For now, return null as this requires server-side implementation
    return null;
  } catch {
    return null;
  }
}

// Download utilities
export async function downloadFile(attachment: FileAttachment): Promise<void> {
  return await trackFileOperation(
    async () => {
      // Get secure URL with authorization check
      const signedUrl = await getSecureFileUrl(attachment.id, 'download');
      
      // Trigger download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    {
      fileName: attachment.original_filename,
      operation: 'download',
      fileSize: attachment.file_size,
      fileType: attachment.mime_type
    }
  );
}

// File deletion
export async function deleteFile(attachment: FileAttachment): Promise<void> {
  return await trackFileOperation(
    async () => {
      // Log the deletion attempt
      await supabase
        .from('file_access_logs')
        .insert({
          file_id: attachment.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'delete'
        });

      // Soft delete in database (mark as deleted)
      const { error: dbError } = await supabase
        .from('file_attachments')
        .update({ 
          status: 'deleted',
          deleted_at: new Date().toISOString()
        })
        .eq('id', attachment.id);

      if (dbError) {
        throw new Error(dbError.message);
      }

      // Remove from storage
      const { error: storageError } = await supabase.storage
        .from('attachments')
        .remove([attachment.storage_path]);

      if (storageError) {
        console.warn('Failed to remove file from storage:', storageError);
        // Don't throw here as the database update succeeded
      }
    },
    {
      fileName: attachment.original_filename,
      operation: 'delete',
      fileSize: attachment.file_size,
      fileType: attachment.mime_type
    }
  );
}